/**
 * GameSelector 游戏选择器组件
 * @description 提供游戏目录的单选/多选功能，支持图文展示模式
 * @module dashboard/components/GameSelector
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useSelector } from 'umi';
import ImageTextSelector from '@home/components/ImageTextSelector';
import type { IAllConfig, IGameSelectedInfo, IGameSelectedInfoItem, IGameSelectorProps, IOptionItem } from './type';

/** 默认"全部"选项的值 */
const DEFAULT_ALL_VALUE = '255';

/**
 * 标准化值列表
 * @description 将任意类型的值转换为字符串数组格式
 * @param value - 需要标准化的值，可以是数组、字符串或其他类型
 * @returns 标准化后的字符串数组
 */
const normalizeValueList = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item));
  }
  if (value === undefined || value === null || value === '') {
    return [];
  }
  return [String(value)];
};

/**
 * 获取游戏封面图片的键列表
 * @description 从游戏对象中提取可用于查找封面图片的键值列表
 * @param game - 游戏对象，包含 sn、game_code、code 等属性
 * @returns 有效的键值字符串数组
 */
const getCoverKeyList = (game: any): string[] => {
  const keyList = [game?.sn, game?.game_code, game?.code].filter((item) => item !== undefined && item !== null && item !== '');
  return keyList.map((item) => String(item));
};

/**
 * 游戏选择器组件
 * @description 基于 ImageTextSelector 封装的游戏选择组件，
 * 支持从 Redux store 中获取游戏列表并展示为图文选择器
 * @param props - 组件属性
 * @returns React 组件
 */
const GameSelector: React.FC<IGameSelectorProps> = (props) => {
  const {
    optionAll,
    defaultValue = [DEFAULT_ALL_VALUE],
    mode = 'multiple',
    fallbackImageMap = {},
    tooltipMap: tooltipMapProp = {},
    style,
    className,
    data,
    changeData,
    changeDebounceMs = 500,
  } = props;
  // 优先使用顶层 data 贯穿下来的 catalogTooltipMap（页面级共享配置），兜底 props.tooltipMap
  const tooltipMap = (data?.catalogTooltipMap as Record<string, { en: string; cn: string }> | undefined) ?? tooltipMapProp;

  // 从 Redux store 获取选择器配置和原始游戏列表
  const { selector, rawGameList } = useSelector((state: any) => {
    return {
      selector: state?.dashboardSetting?.selector,
      rawGameList: state?.dashboard?.rawGameList || [],
    };
  }, shallowEqual);

  // 获取游戏目录列表，优先使用自定义过滤器中的目录
  const catalogList = useMemo(() => {
    const customCatalog = selector?.custom_filters?.catalog;
    if (Array.isArray(customCatalog)) {
      return customCatalog;
    }
    if (Array.isArray(selector?.catalog)) {
      return selector?.catalog;
    }
    return [];
  }, [selector]);

  // 构建游戏封面图片映射表，用于快速查找游戏封面
  const gameCoverMap = useMemo(() => {
    const coverMap = new Map<string, string>();

    // 收集单个游戏的封面图片
    const collectCover = (game: any) => {
      const cover = game?.cover;
      if (!cover) {
        return;
      }
      const keyList = getCoverKeyList(game);
      keyList.forEach((key) => {
        if (!coverMap.has(key)) {
          coverMap.set(key, cover);
        }
      });
    };

    // 递归收集游戏列表中的封面图片
    const collectFromList = (list: any[]) => {
      list?.forEach((item) => {
        collectCover(item);
        collectFromList(item?.game_List || []);
        collectFromList(item?.game_list || []);
      });
    };

    collectFromList(rawGameList || []);
    return coverMap;
  }, [rawGameList]);

  // 构建选项列表，包含游戏名称、封面等信息
  const optionsList = useMemo<IOptionItem[]>(() => {
    return (catalogList || []).reduce((result: IOptionItem[], item: any) => {
      const value = String(item?.code || '');
      if (!value) {
        return result;
      }

      const nameEn = item?.name_en || item?.name_ch || value;
      const nameCn = item?.name_ch || item?.name_en || value;
      const sn = String(item?.sn || '');
      // 按优先级获取封面图片：sn > code > 备用映射表
      const image = (sn && gameCoverMap.get(sn))
        || gameCoverMap.get(value)
        || fallbackImageMap?.[sn]
        || fallbackImageMap?.[value]
        || fallbackImageMap?.[nameEn]
        || fallbackImageMap?.[nameCn]
        || '';

      result.push({
        value,
        nameEn,
        nameCn,
        image,
        tooltipEn: tooltipMap?.[nameEn]?.en || tooltipMap?.[nameCn]?.en || undefined,
        tooltipCn: tooltipMap?.[nameEn]?.cn || tooltipMap?.[nameCn]?.cn || undefined,
      });
      return result;
    }, []);
  }, [catalogList, fallbackImageMap, gameCoverMap, tooltipMap]);

  // 标准化"全部"选项配置
  const normalizedOptionAll = useMemo<IAllConfig | undefined>(() => {
    if (!optionAll?.value) {
      return undefined;
    }

    return {
      value: String(optionAll.value),
      nameEn: optionAll?.nameEn || '',
      nameCn: optionAll?.nameCn || '',
      image: optionAll?.image,
      disabled: optionAll?.disabled,
    };
  }, [optionAll]);

  // 计算当前激活的目录选项
  const activeCatalog = useMemo(() => {
    // data.catalog 可能是对象 { values, gameSelectedInfo } 或数组（兼容旧格式）
    const rawCatalog = data?.catalog;
    const catalogValue = rawCatalog?.values ?? rawCatalog;
    const catalogFromData = normalizeValueList(catalogValue);
    if (catalogFromData.length > 0) {
      return catalogFromData;
    }

    const defaultCatalog = normalizeValueList(defaultValue);
    if (defaultCatalog.length > 0) {
      return defaultCatalog;
    }

    if (normalizedOptionAll?.value) {
      return [String(normalizedOptionAll.value)];
    }

    return [DEFAULT_ALL_VALUE];
  }, [data?.catalog, defaultValue, normalizedOptionAll]);

  // 生成选择器唯一标识，仅在选项列表变化时重建组件，避免选择变化导致的闪烁
  const selectorKey = useMemo(() => {
    return optionsList.map((item: any) => item.value).join(',');
  }, [optionsList]);

  // 构建选中游戏的完整信息
  const buildGameSelectedInfo = useCallback((selectedValues: string[]): IGameSelectedInfo => {
    const allValue = normalizedOptionAll?.value || DEFAULT_ALL_VALUE;
    const isAllSelected = selectedValues.includes(allValue);

    // 如果选中了"全部"，返回 optionsList 所有项（不包含"全部"选项本身）
    if (isAllSelected) {
      const list: IGameSelectedInfoItem[] = optionsList.map((item) => ({
        value: item.value,
        nameEn: item.nameEn,
        nameCn: item.nameCn,
        image: item.image || '',
      }));

      return {
        list,
        values: optionsList.map((item) => item.value),
      };
    }

    // 非"全部"情况，按选中值匹配
    const optionsMap = new Map<string, IGameSelectedInfoItem>();
    optionsList.forEach((item) => {
      optionsMap.set(item.value, {
        value: item.value,
        nameEn: item.nameEn,
        nameCn: item.nameCn,
        image: item.image || '',
      });
    });

    const list = selectedValues
      .map((val) => optionsMap.get(val))
      .filter((item): item is IGameSelectedInfoItem => !!item);

    return {
      list,
      values: selectedValues,
    };
  }, [optionsList, normalizedOptionAll]);

  // 用于记录上一次同步的 catalog 和 optionsList 版本，避免重复触发
  const prevSyncedCatalogRef = useRef<string>('');
  const prevOptionsVersionRef = useRef<number>(0);
  const changeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  // optionsList 版本号，每次 optionsList 变化递增
  const optionsVersion = useMemo(() => {
    return optionsList.length > 0 ? optionsList.length : 0;
  }, [optionsList]);

  // 统一同步 catalog 数据，避免散落在多个回调里
  const syncCatalogData = useCallback((value: string[]) => {
    changeData?.('catalog', {
      values: value,
      gameSelectedInfo: buildGameSelectedInfo(value),
    });
    prevSyncedCatalogRef.current = value.join(',');
    prevOptionsVersionRef.current = optionsVersion;
  }, [changeData, buildGameSelectedInfo, optionsVersion]);

  const normalizedDebounceMs = useMemo(() => {
    if (typeof changeDebounceMs !== 'number' || Number.isNaN(changeDebounceMs)) {
      return 0;
    }
    return Math.max(0, changeDebounceMs);
  }, [changeDebounceMs]);

  // 处理选择变更事件（合并 catalog 值和 gameSelectedInfo 为一次 changeData 调用）
  const handleChange = useCallback((value: string[]) => {
    if (changeTimerRef.current) {
      clearTimeout(changeTimerRef.current);
    }
    if (normalizedDebounceMs <= 0) {
      syncCatalogData(value);
      return;
    }
    changeTimerRef.current = setTimeout(() => {
      syncCatalogData(value);
      changeTimerRef.current = undefined;
    }, normalizedDebounceMs);
  }, [normalizedDebounceMs, syncCatalogData]);

  useEffect(() => {
    return () => {
      if (changeTimerRef.current) {
        clearTimeout(changeTimerRef.current);
      }
    };
  }, []);

  // 初始化时及 activeCatalog/optionsList 变化时同步 gameSelectedInfo
  useEffect(() => {
    // optionsList 未就绪时不触发，避免传空的 gameSelectedInfo
    if (optionsList.length === 0) {
      return;
    }
    const catalogKey = activeCatalog.join(',');
    // catalogKey 变化 或 optionsList 变化（同一个 catalogKey 但游戏列表刚加载完）都需要重新同步
    const isOptionsChanged = optionsVersion !== prevOptionsVersionRef.current;
    if (catalogKey === prevSyncedCatalogRef.current && !isOptionsChanged) {
      return;
    }
    if (changeTimerRef.current) {
      clearTimeout(changeTimerRef.current);
      changeTimerRef.current = undefined;
    }
    syncCatalogData(activeCatalog);
  }, [activeCatalog, optionsList, optionsVersion, syncCatalogData]);

  return (
    <ImageTextSelector
      key={selectorKey}
      optionAll={normalizedOptionAll}
      optionsList={optionsList}
      defaultValue={activeCatalog}
      mode={mode}
      onChange={handleChange}
      style={style}
      className={className}
    />
  );
};

export default GameSelector;
